import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

// Material Imports
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DialogModule } from '@angular/cdk/dialog';

// Third Party
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';

// Library and Local Configuration
import { CardSoftwareLibraryModule, LOGIN_AUTH_SERVICE, LOGIN_CONFIG, SierraTheme } from '@card-software/library';
import { siteInterceptor, errorInterceptor, SITE_API_BASE_URL } from '@card-software/nexus-utilities';
import { PrimengModule } from './primeng/primeng.module';
import { AppRoutingModule } from './app-routing.module';
import { authInterceptor } from './auth.interceptor';
import { UserAuthenticationService } from './services/user-authentication.service';
import { CustomTranslateLoader } from './services/custom.translate.loader';
import { TranslateWithFallbackPipe } from './pipes/translate-with-fallback.pipe';
import { environment } from '../environments/environment';

// Components
import { AppComponent } from './app.component';
import { AssetsComponent } from './components/assets/assets.component';
import { SitesComponent } from './components/sites/sites.component';
import { AssetDetailsComponent } from './components/asset-details/asset-details.component';
import { AssetsDetailsComponent } from './components/assets-details/assets-details.component';
import { AssetFamilyDetailsComponent } from './components/asset-management-module/asset-family-details/asset-family-details.component';

// Modals
import { SiteDetailsModalComponent } from './components/_modals/site-details-modal/site-details-modal.component';
import { CategoryDetailsModalComponent } from './components/_modals/category-details-modal/category-details-modal.component';
import { TypeDetailsModalComponent } from './components/_modals/type-details-modal/type-details-modal.component';
import { TypeDetailsProcessModalComponent } from './components/_modals/type-details-process-modal/type-details-process-modal.component';
import { ModelDetailsModalComponent } from './components/_modals/model-details-modal/model-details-modal.component';
import { ModelPropertyDetailsModalComponent } from './components/_modals/model-property-details-modal/model-property-details-modal.component';
import { ModelDetailsProcessModalComponent } from './components/_modals/model-details-process-modal/model-details-process-modal.component';

@NgModule({
  declarations: [
    AppComponent,
    AssetDetailsComponent,
    AssetFamilyDetailsComponent,
    AssetsComponent,
    SitesComponent,
    SiteDetailsModalComponent,
    AssetsDetailsComponent,
    CategoryDetailsModalComponent,
    TypeDetailsModalComponent,
    TypeDetailsProcessModalComponent,
    ModelDetailsModalComponent,
    ModelPropertyDetailsModalComponent,
    ModelDetailsProcessModalComponent,
  ],
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    CardSoftwareLibraryModule,
    CommonModule,
    DialogModule,
    FormsModule,
    MatAutocompleteModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    ReactiveFormsModule,
    PrimengModule,
    TranslateWithFallbackPipe,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateLoader,
      },
    }),
  ],
  providers: [
    { provide: LOGIN_AUTH_SERVICE, useClass: UserAuthenticationService },
    {
      provide: LOGIN_CONFIG,
      useValue: {
        domainAppName: 'Sierra Asset',
        contactEmail: 'it-support-sierra-textiles@card-software.com',
        redirectUrl: '/',
      },
    },
    TranslateService,
    provideHttpClient(withInterceptors([authInterceptor, siteInterceptor, errorInterceptor])),
    { provide: SITE_API_BASE_URL, useValue: environment.proxyUrls.userMasterApi },
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: SierraTheme,
        options: {
          darkModeSelector: false,
        },
      },
    }),
    MessageService,
    ConfirmationService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
